import React from "react";
import TextField from "@/components/common/text-field";
import AddFieldDropdown from "./AddFieldDropdown";
import SectionHeader from "./SectionHeader";
import FieldCard from "./FieldCard";
import FieldConfigModal from "./FieldConfigModal";
import type { FormField, FormPage } from "../types";
import BasicRichTextEditor from "@/components/common/editor/basic-rich-text-editor";
import { DndContext, closestCenter, type DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface SectionDetailsProps {
  section: {
    id: string;
    name: string;
    description: string;
    fields: FormField[];
  };
  sectionIndex: number;
  onChange: (
    id: string,
    updates: { name?: string; description?: string },
  ) => void;
  onAddField: (type: string) => void;
  onDeleteField: (sectionId: string, fieldId: string) => void;
  onUpdateField: (sectionId: string, updatedField: FormField) => void;
  onReorderFields?: (sectionId: string, newFields: FormField[]) => void;
  showGrip?: boolean;
  showTrash?: boolean;
  onDelete?: () => void;
  gripProps?: React.HTMLAttributes<HTMLSpanElement>;
  isDraggingSection?: boolean;
  formData?: FormPage[];
}

const getFieldTypeInfo = (type: string) => {
  const fieldTypeMap: Record<string, { label: string; icon: string }> = {
    text: { label: "Campo de texto", icon: "text" },
    number: { label: "Campo numérico", icon: "hashtag" },
    textarea: { label: "Área de texto (párrafo)", icon: "paragraph" },
    date: { label: "Fecha y hora", icon: "calendar-clock" },
    datetime: { label: "Fecha y hora", icon: "calendar-clock" },
    datetime_range: { label: "Rango de fechas", icon: "calendars" },
    date_range: { label: "Rango de fechas", icon: "calendars" },
    email: { label: "Correo electrónico", icon: "envelope" },
    phone: { label: "Número telefónico", icon: "phone" },
    identification: { label: "Identificación", icon: "id-card" },
    file: { label: "Subida de archivos", icon: "file-arrow-up" },
    select: { label: "Campo Select", icon: "list" },
    interoperability: { label: "Interoperabilidad", icon: "diagram-nested" },
    switch: { label: "Campo Switch", icon: "toggle-on" },
  };
  return fieldTypeMap[type] || { label: "Campo", icon: "question" };
};

// Sortable wrapper for FieldCard
interface SortableFieldCardProps {
  field: FormField;
  fieldInfo: { label: string; icon: string };
  onDeleteField: (sectionId: string, fieldId: string) => void;
  sectionId: string;
  onEditField: (sectionId: string, field: FormField) => void;
  isDraggingField: boolean;
}

const SortableFieldCard: React.FC<SortableFieldCardProps> = ({
  field,
  fieldInfo,
  onDeleteField,
  sectionId,
  onEditField,
  isDraggingField,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id || "" });

  const style = {
    transform: transform ? CSS.Transform.toString(transform) : undefined,
    transition,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <FieldCard
        field={field}
        fieldInfo={fieldInfo}
        onDeleteField={onDeleteField}
        sectionId={sectionId}
        onEditField={onEditField}
        showGrip={true}
        gripProps={{ ...listeners, ...attributes }}
        isDragging={isDraggingField}
      />
    </div>
  );
};

export const SectionDetails: React.FC<SectionDetailsProps> = ({
  section,
  sectionIndex,
  onChange,
  onAddField,
  onDeleteField,
  onUpdateField,
  onReorderFields,
  showGrip = false,
  showTrash = false,
  onDelete,
  gripProps,
  isDraggingSection = false,
  formData,
}) => {
  const [editField, setEditField] = React.useState<FormField | null>(null);
  const [editFieldSectionId, setEditFieldSectionId] = React.useState<
    string | null
  >(null);
  const [showEditModal, setShowEditModal] = React.useState(false);
  const [isDraggingField, setIsDraggingField] = React.useState(false);

  // Local state for section name/description
  const [localName, setLocalName] = React.useState(section.name);
  const [localDescription, setLocalDescription] = React.useState(
    section.description,
  );

  React.useEffect(() => {
    setLocalName(section.name);
  }, [section.id, section.name]);

  React.useEffect(() => {
    setLocalDescription(section.description);
  }, [section.id, section.description]);

  const handleEditField = (sectionId: string, field: FormField) => {
    setEditField(field);
    setEditFieldSectionId(sectionId);
    setShowEditModal(true);
  };

  const handleEditFieldSave = (updatedField: FormField) => {
    if (!editFieldSectionId) return;
    onUpdateField(section.id, updatedField);
    setShowEditModal(false);
    setEditField(null);
    setEditFieldSectionId(null);
  };

  const handleEditFieldClose = () => {
    setShowEditModal(false);
    setEditField(null);
    setEditFieldSectionId(null);
  };

  if (isDraggingSection) {
    return (
      <SectionHeader
        showGrip={showGrip}
        gripProps={gripProps}
        showTrash={showTrash}
        onDelete={onDelete}
        sectionIndex={sectionIndex}
      />
    );
  }
  return (
    <div className="py-2">
      {/* Header row with drag handle, title, and trash icon */}
      {(showGrip || showTrash) && (
        <SectionHeader
          showGrip={showGrip}
          gripProps={gripProps}
          showTrash={showTrash}
          onDelete={onDelete}
          sectionIndex={sectionIndex}
        />
      )}
      {/* Section content with transition */}
      <div
        className={`px-4 mb-1 transition-all duration-300 ease-in-out pb-2 overflow-hidden ${isDraggingSection ? "max-h-0 opacity-0 scale-y-95" : "max-h-full opacity-100 scale-y-100"}`}
      >
        <div className="mb-4">
          <TextField
            id={`title-${section.id}`}
            label="Título de la sección"
            required
            value={localName}
            onChange={(e) => setLocalName(e.target.value)}
            onBlur={() => {
              if (localName !== section.name) {
                onChange(section.id, { name: localName });
              }
            }}
            placeholder="Escribe el título de la sección"
            maxLength={50}
          />
        </div>
        <div className="mb-4">
          <BasicRichTextEditor
            label="Descripción de la sección"
            required
            value={localDescription}
            onChange={(content) => setLocalDescription(content)}
            onBlur={() => {
              if (localDescription !== section.description) {
                onChange(section.id, { description: localDescription });
              }
            }}
            placeholder="Escribe la descripción de la sección"
            minHeight="120px"
            maxHeight="220px"
            showAlignment
          />
          {/* <TextAreaField
            id={`desc-${section.id}`}
            label="Descripción de la sección"
            required
            value={localDescription}
            onChange={(e) => setLocalDescription(e.target.value)}
            onBlur={() => {
              if (localDescription !== section.description) {
                onChange(section.id, { description: localDescription });
              }
            }}
            placeholder="Escribe la descripción de la sección"
            rows={3}
          /> */}
        </div>
        {/* Display saved fields as cards */}
        {section.fields && section.fields.length > 0 && (
          <div className="mb-4 space-y-3">
            {section.fields.length > 1 && onReorderFields ? (
              <DndContext
                collisionDetection={closestCenter}
                onDragStart={() => setIsDraggingField(true)}
                onDragEnd={(event: DragEndEvent) => {
                  setIsDraggingField(false);
                  const { active, over } = event;
                  if (!over || active.id === over.id) return;
                  const oldIndex = section.fields.findIndex(
                    (f) => f.id === active.id,
                  );
                  const newIndex = section.fields.findIndex(
                    (f) => f.id === over.id,
                  );
                  if (oldIndex === -1 || newIndex === -1) return;
                  const newFields = arrayMove(
                    section.fields,
                    oldIndex,
                    newIndex,
                  );
                  const updatedFields = newFields.map((f, index, arr) => {
                    if (!f.conditions) return f;
                    const conditions = f.conditions.filter((cond) => {
                      const condVarId = cond?.data?.selectedVar?.id;
                      const fieldIndex = arr.findIndex(
                        (field) =>
                          (field.technicalKey || field.name) === condVarId,
                      );
                      if (fieldIndex === -1) return true;
                      if (fieldIndex < index) return true;
                      return false;
                    });

                    return {
                      ...f,
                      conditions,
                      isConditional: conditions.length > 0,
                    };
                  });
                  onReorderFields(section.id, updatedFields);
                }}
              >
                <SortableContext items={section.fields.map((f) => f.id || "")}>
                  {section.fields.map((field) => {
                    const fieldInfo = getFieldTypeInfo(field.type);
                    return (
                      <SortableFieldCard
                        key={field.id}
                        field={field}
                        fieldInfo={fieldInfo}
                        onDeleteField={onDeleteField}
                        sectionId={section.id}
                        onEditField={handleEditField}
                        isDraggingField={isDraggingField}
                      />
                    );
                  })}
                </SortableContext>
              </DndContext>
            ) : (
              section.fields.map((field) => {
                const fieldInfo = getFieldTypeInfo(field.type);
                return (
                  <FieldCard
                    key={field.id}
                    field={field}
                    fieldInfo={fieldInfo}
                    onDeleteField={onDeleteField}
                    sectionId={section.id}
                    onEditField={handleEditField}
                  />
                );
              })
            )}
          </div>
        )}
        <AddFieldDropdown onAddField={onAddField} />
      </div>
      {showEditModal && editField && (
        <FieldConfigModal
          formData={formData}
          open={showEditModal}
          type={editField.type}
          sectionId={section.id}
          onClose={handleEditFieldClose}
          onSave={handleEditFieldSave}
          initialField={editField}
        />
      )}
    </div>
  );
};
